import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { mdiChevronDown, mdiChevronUp } from '@lumapps/lumx/icons';
import { Button, ButtonProps, Emphasis, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import './index.scss';

export interface SeeMoreListProps extends ButtonProps {
    /** list of items to display */
    items: any[];
    /** maximum amount of items to display before showing the `See more` */
    maxItems: number;
    /** custom item rendered, overriding the default one */
    itemRenderer?: (item: any, index: number, isLast?: boolean) => React.ReactNode | string;
    /** custom classname */
    className?: string;
    /** label to use instead of the `See more` */
    moreLabelKey?: string;
    /** label to use instead of the `See less` */
    lessLabelKey?: string;
    /** specific component to render instead of the basic Button */
    customButton?: React.ElementType | string;
    /** whether the button displays a chevron before the text or not */
    withChevron?: boolean;
    /** component to be rendered after this component */
    after?: React.ReactNode;
    /** component to be rendered before this component */
    before?: React.ReactNode;
}

const CLASSNAME = 'lumx-see-more-list';
/**
 * Component that applies a logic of limiting the total amount of elements rendered
 * depending on the provided maximum. If the total amount of items exceeds that maximum,
 * a button with a `See more` wording will be displayed. Upon clicking on the button,
 * all items are displayed.
 *
 * @family Lists
 * @param SeeMoreListProps
 * @returns SeeMoreList
 */
export const SeeMoreList: React.FC<SeeMoreListProps> = ({
    items,
    maxItems,
    itemRenderer,
    className,
    moreLabelKey = GLOBAL.SEE_MORE_NB_ITEMS,
    lessLabelKey = GLOBAL.SHOW_LESS,
    customButton,
    withChevron = false,
    after,
    before,
    ...rest
}) => {
    const { element } = useClassnames(CLASSNAME);
    const { translateAndReplace, translateKey } = useTranslate();
    const [nbOfItemsToDisplay, setNOfItemsToDisplay] = React.useState(maxItems);
    const restrictedItems = React.useMemo(() => {
        return items.slice(0, nbOfItemsToDisplay);
    }, [items, nbOfItemsToDisplay]);

    const showMore = () => {
        setNOfItemsToDisplay(items.length);
    };

    const showLess = () => {
        setNOfItemsToDisplay(maxItems);
    };

    const restOfItems = items.length - nbOfItemsToDisplay;
    const ButtonComponent = customButton || Button;

    return (
        <>
            {before}

            {restrictedItems.map((item, index) =>
                itemRenderer ? itemRenderer(item, index, index === restrictedItems.length - 1) : item,
            )}

            {restOfItems > 0 ? (
                <ButtonComponent
                    onClick={showMore}
                    emphasis={Emphasis.low}
                    size={Size.s}
                    className={element('button', [className])}
                    leftIcon={withChevron ? mdiChevronDown : undefined}
                    {...(!customButton ? { hasBackground: false } : {})}
                    {...rest}
                >
                    {translateAndReplace(moreLabelKey, { count: restOfItems })}
                </ButtonComponent>
            ) : null}

            {nbOfItemsToDisplay > maxItems && restOfItems === 0 ? (
                <ButtonComponent
                    onClick={showLess}
                    emphasis={Emphasis.low}
                    size={Size.s}
                    className={element('button', [className])}
                    leftIcon={withChevron ? mdiChevronUp : undefined}
                    {...(!customButton ? { hasBackground: false } : {})}
                    {...rest}
                >
                    {translateKey(lessLabelKey)}
                </ButtonComponent>
            ) : null}

            {after}
        </>
    );
};
